import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { useAuth, hasFullAccess } from '../context/AuthContext';
import { API_URL as API } from '../config/env.js';
import { ensureMonthlyPaperAccess, getPdfBytes } from '../utils/paperCache';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  typeof window !== 'undefined' ? `${window.location.origin}/pdf.worker.min.mjs` : '/pdf.worker.min.mjs';

const coverCache = new Map();

export default function MagazineCover({ monthKey, className = '', fill = false }) {
  const canvasRef = useRef(null);
  const { user } = useAuth();
  const fullAccess = hasFullAccess(user);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!monthKey || !user?.token || !fullAccess) return undefined;
    let cancelled = false;
    const canvas = canvasRef.current;

    (async () => {
      try {
        const cacheKey = `${monthKey}|${fill ? 'fill' : 'fit'}`;
        let dataUrl = coverCache.get(cacheKey);
        if (!dataUrl) {
          const access = await ensureMonthlyPaperAccess(API, user.token, monthKey);
          const bytes = await getPdfBytes(access.pdfUrl);
          const data = bytes instanceof ArrayBuffer ? new Uint8Array(bytes.slice(0)) : bytes.slice(0);
          const pdf = await pdfjsLib.getDocument({ data }).promise;
          const page = await pdf.getPage(1);
          const base = page.getViewport({ scale: 1 });
          const boxW = fill ? 360 : 220;
          const boxH = fill ? 480 : Math.round((220 * 4) / 3);
          const scale = fill
            ? Math.max(boxW / base.width, boxH / base.height)
            : Math.min(boxW / base.width, boxH / base.height);
          const viewport = page.getViewport({ scale });
          const off = document.createElement('canvas');
          off.width = boxW;
          off.height = boxH;
          const ctx = off.getContext('2d');
          ctx.fillStyle = '#1a1a1e';
          ctx.fillRect(0, 0, boxW, boxH);
          const ox = Math.floor((boxW - viewport.width) / 2);
          const oy = Math.floor((boxH - viewport.height) / 2);
          ctx.translate(ox, oy);
          await page.render({ canvasContext: ctx, viewport }).promise;
          dataUrl = off.toDataURL('image/jpeg', 0.86);
          coverCache.set(cacheKey, dataUrl);
        }
        if (cancelled || !canvas) return;
        const img = new Image();
        img.onload = () => {
          if (cancelled || !canvasRef.current) return;
          const el = canvasRef.current;
          const ctx = el.getContext('2d');
          el.width = img.width;
          el.height = img.height;
          ctx.drawImage(img, 0, 0);
          setReady(true);
        };
        img.src = dataUrl;
      } catch {
        if (!cancelled) setReady(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [monthKey, user?.token, fullAccess, fill]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      data-ready={ready ? '1' : '0'}
      data-fill={fill ? '1' : '0'}
      aria-hidden
    />
  );
}
